import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, ArrowRight, FileText, TrendingUp, Loader, AlertCircle } from 'lucide-react';
import { useNotes } from '../hooks/useApi';
import NoteCard from './NoteCard';

const RecentNotes: React.FC = () => {
  const navigate = useNavigate();
  const popular = useNotes({ sort: 'downloads', minDownloads: 1, limit: 6 });
  const fallback = useNotes({
    sort: 'newest',
    limit: 6,
    enabled: !popular.loading && !popular.error && popular.notes.length === 0,
  });

  const notes = popular.notes.length > 0 ? popular.notes : fallback.notes;
  const loading = popular.loading || (popular.notes.length === 0 && fallback.loading);
  const error = popular.error || fallback.error;
  const showingFallback = popular.notes.length === 0 && notes.length > 0;

  const handleViewAll = () => {
    navigate('/notlar');
  };

  return (
    <section style={{ padding: '80px 24px 100px', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '-5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.04) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: '1300px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '48px',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 18px',
                borderRadius: '50px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#06b6d4',
                background: 'rgba(6, 182, 212, 0.08)',
                border: '1px solid rgba(6, 182, 212, 0.2)',
                marginBottom: '16px',
              }}
            >
              <TrendingUp size={14} />
              <span>{showingFallback ? 'En Yeni' : 'En Popüler'}</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                fontWeight: 800,
                margin: 0,
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
              }}
            >
              <span className="gradient-text-white">
                {showingFallback ? 'Son Eklenen Notlar' : 'En Çok İndirilen Notlar'}
              </span>
            </motion.h2>
          </div>

          <motion.button
            type="button"
            onClick={handleViewAll}
            whileHover={{ scale: 1.05, borderColor: 'var(--accent-primary-glow)', background: 'var(--accent-primary-soft)' }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            style={{
              color: 'var(--accent-primary)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              padding: '12px 22px',
              borderRadius: '12px',
              border: '1px solid var(--accent-primary-border)',
              background: 'var(--accent-primary-soft)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Eye size={16} />
            <span>Tümünü Görüntüle</span>
            <ArrowRight size={14} />
          </motion.button>
        </div>

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              gap: '16px',
            }}
          >
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
              <Loader size={32} color="var(--accent-primary)" />
            </motion.div>
            <p style={{ color: '#64748b', fontSize: '15px' }}>Notlar yükleniyor...</p>
          </motion.div>
        )}

        {!loading && error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glow-card"
            style={{ padding: '48px 32px', borderRadius: '20px', textAlign: 'center' }}
          >
            <AlertCircle size={40} color="#ef4444" style={{ marginBottom: '16px', opacity: 0.7 }} />
            <h3 style={{ color: '#e2e8f0', fontSize: '1.1rem', marginBottom: '8px' }}>Bağlantı Hatası</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>{error}</p>
          </motion.div>
        )}

        {!loading && !error && notes.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glow-card"
            style={{ padding: '64px 32px', borderRadius: '20px', textAlign: 'center' }}
          >
            <FileText size={48} color="#334155" style={{ marginBottom: '16px' }} />
            <h3 style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '8px' }}>Henüz onaylı not yok</h3>
            <p style={{ color: '#475569', fontSize: '14px' }}>İlk notu siz yükleyebilirsiniz.</p>
          </motion.div>
        )}

        {!loading && !error && notes.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
              gap: '24px',
            }}
          >
            {notes.map((note, index) => (
              <NoteCard key={note._id} note={note} index={index} registerPromptOnDownload />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RecentNotes;
